import Component from "@glimmer/component";
import { fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import PluginOutlet from "discourse/components/plugin-outlet";
import DMenu from "discourse/float-kit/components/d-menu";
import lazyHash from "discourse/helpers/lazy-hash";
import { allLevels, buttonDetails } from "discourse/lib/notification-levels";
import { applyValueTransformer } from "discourse/lib/transformer";
import DButton from "discourse/ui-kit/d-button";
import DDropdownMenu from "discourse/ui-kit/d-dropdown-menu";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

function constructKey(prefix, level, suffix, key) {
  let string = prefix + "." + level;

  if (suffix) {
    string += suffix;
  }

  return i18n(string + "." + key);
}

class NotificationsTrackingTrigger extends Component {
  @service site;

  get showFullTitle() {
    return this.args.showFullTitle ?? true;
  }

  get showCaret() {
    return this.site.desktopView && (this.args.showCaret ?? true);
  }

  get title() {
    return constructKey(
      this.args.prefix,
      this.args.selectedLevel.key,
      this.args.suffix,
      "title"
    );
  }

  get tooltip() {
    return i18n("notifications_tracking.tooltip", { level: this.title });
  }

  <template>
    <button
      class={{dConcatClass
        "btn btn-default"
        (if this.showFullTitle "btn-icon-text" "no-text")
      }}
      title={{this.tooltip}}
      ...attributes
    >
      {{dIcon @selectedLevel.icon}}

      {{#if this.showFullTitle}}
        <span class="d-button-label">
          {{this.title}}
        </span>
      {{/if}}

      {{#if this.showCaret}}
        {{dIcon "angle-down" class="notifications-tracking-btn__caret"}}
      {{/if}}
    </button>
  </template>
}

export default class NotificationsTracking extends Component {
  get selectedLevel() {
    return buttonDetails(this.args.levelId);
  }

  get levels() {
    return this.args.levels ?? allLevels;
  }

  @action
  registerDmenuApi(api) {
    this.dmenuApi = api;
  }

  @action
  async setNotificationLevel(level) {
    await this.dmenuApi.close({ focusTrigger: true });
    this.args.onChange?.(level);
  }

  @action
  description(level) {
    return applyValueTransformer(
      "notifications-tracking-description",
      constructKey(
        this.args.prefix,
        level.key,
        this.args.suffix,
        "description"
      ),
      { topic: this.args.topic, level, prefix: this.args.prefix }
    );
  }

  @action
  label(level) {
    return constructKey(this.args.prefix, level.key, this.args.suffix, "title");
  }

  @action
  isSelectedClass(level) {
    return this.args.levelId === level.id ? "-selected" : "";
  }

  <template>
    <DMenu
      data-level-id={{this.selectedLevel.id}}
      data-level-name={{this.selectedLevel.key}}
      ...attributes
      @autofocus={{false}}
      @contentClass={{@contentClass}}
      @identifier="notifications-tracking"
      @modalForMobile={{true}}
      @onRegisterApi={{this.registerDmenuApi}}
      @title={{@title}}
      @triggerClass={{dConcatClass
        "btn-default"
        "btn-icon"
        "notifications-tracking-trigger-btn"
        @triggerClass
      }}
      @triggerComponent={{component
        NotificationsTrackingTrigger
        showFullTitle=@showFullTitle
        showCaret=@showCaret
        selectedLevel=this.selectedLevel
        suffix=@suffix
        prefix=@prefix
      }}
    >
      <:content>
        <DDropdownMenu as |dropdown|>
          {{#each this.levels as |level|}}
            <dropdown.item>
              <DButton
                class={{dConcatClass
                  "notifications-tracking-btn"
                  (this.isSelectedClass level)
                }}
                data-level-id={{level.id}}
                data-level-name={{level.key}}
                @action={{fn this.setNotificationLevel level.id}}
              >
                <div class="notifications-tracking-btn__icons">
                  <PluginOutlet
                    @name="notifications-tracking-icons"
                    @outletArgs={{lazyHash
                      selectedLevelId=@levelId
                      level=level
                      topic=@topic
                    }}
                  >
                    {{dIcon level.icon}}
                  </PluginOutlet>
                </div>
                <div class="notifications-tracking-btn__texts">
                  <span class="notifications-tracking-btn__label">
                    {{this.label level}}
                  </span>
                  <span class="notifications-tracking-btn__description">
                    {{this.description level}}
                  </span>
                </div>
              </DButton>
            </dropdown.item>
          {{/each}}
        </DDropdownMenu>
      </:content>
    </DMenu>
  </template>
}
